import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Check,
  Zap,
  Shield,
  Clock,
  TrendingUp,
  Users,
  BarChart3,
  Calendar,
  Sparkles,
  Menu,
  X,
  ChevronDown,
  FileText
} from 'lucide-react';
import Footer from '../components/Footer';
import FlowAnimation from '../components/FlowAnimation';
import LanguageSwitcher from '../components/LanguageSwitcher';

const LandingPage = () => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleHashtagIndex, setVisibleHashtagIndex] = useState(0);

  // Hashtag rotation effect - 6 hashtag, 2'li gruplar
  const allHashtags = [
    ['#trending', '#viral'],
    ['#socialmedia', '#marketing'],
    ['#engagement', '#instagram']
  ];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setVisibleHashtagIndex((prev) => (prev + 1) % allHashtags.length);
    }, 4000); // Her 4 saniyede bir değiş (2s göster + 2s geçiş)
    return () => clearInterval(interval);
  }, [allHashtags.length]);
  const [docsDropdownOpen, setDocsDropdownOpen] = useState(false);

  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: t('landing.features.multiPlatform.title'),
      description: t('landing.features.multiPlatform.description'),
      color: 'from-blue-500 to-cyan-500',
      animation: (
        <div className="flex gap-2 mt-4 justify-center flex-wrap">
          {[
            { color: 'from-pink-500 to-purple-600', icon: (
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="white">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            )},
            { color: 'from-blue-500 to-blue-700', icon: (
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="white">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            )},
            { color: 'from-gray-800 to-black', icon: (
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="white">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            )},
            { color: 'from-blue-600 to-blue-800', icon: (
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="white">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            )},
            { color: 'from-red-500 to-red-700', icon: (
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="white">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            )}
          ].map((platform, i) => (
            <div
              key={i}
              className={`w-10 h-10 rounded-lg bg-gradient-to-br ${platform.color} flex items-center justify-center shadow-lg`}
              style={{
                animation: `pulse 2s ease-in-out infinite`,
                animationDelay: `${i * 0.2}s`
              }}
            >
              {platform.icon}
            </div>
          ))}
        </div>
      )
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: t('landing.features.smartSchedule.title'),
      description: t('landing.features.smartSchedule.description'),
      color: 'from-purple-500 to-pink-500',
      animation: (
        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-progress"></div>
          </div>
          <Clock className="w-5 h-5 text-purple-500 animate-spin" style={{ animationDuration: '3s' }} />
        </div>
      )
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: t('landing.features.templates.title'),
      description: t('landing.features.templates.description'),
      color: 'from-yellow-500 to-orange-500',
      animation: (
        <div className="grid grid-cols-3 gap-2 mt-4">
          {[
            { hasImage: true, lines: 3 },
            { hasImage: false, lines: 4 },
            { hasImage: true, lines: 2 }
          ].map((template, i) => (
            <div
              key={i}
              className="h-20 rounded-lg bg-white border-2 border-orange-200 p-2 flex flex-col gap-1 animate-float shadow-sm"
              style={{
                animationDelay: `${i * 0.3}s`,
                animationDuration: '3s'
              }}
            >
              {/* Image placeholder if has image */}
              {template.hasImage && (
                <div className="w-full h-8 bg-gradient-to-br from-yellow-200 to-orange-200 rounded"></div>
              )}
              {/* Text lines */}
              <div className="flex-1 space-y-1">
                {[...Array(template.lines)].map((_, idx) => (
                  <div
                    key={idx}
                    className="h-1 bg-orange-200 rounded"
                    style={{ width: `${Math.random() * 30 + 60}%` }}
                  ></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: t('landing.features.analytics.title'),
      description: t('landing.features.analytics.description'),
      color: 'from-green-500 to-emerald-500',
      animation: (
        <div className="flex items-end gap-2 mt-4 h-16">
          {[
            { height: 40, delay: 0.5 },
            { height: 65, delay: 1.2 },
            { height: 85, delay: 0.1 },
            { height: 50, delay: 1.8 },
            { height: 95, delay: 0.7 },
            { height: 70, delay: 1.4 }
          ].map((bar, i) => (
            <div
              key={i}
              className="flex-1 bg-gradient-to-t from-green-500 to-emerald-400 rounded-t animate-bar-pulse"
              style={{
                height: `${bar.height}%`,
                animationDelay: `${bar.delay}s`
              }}
            ></div>
          ))}
        </div>
      )
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: t('landing.features.oauth.title'),
      description: t('landing.features.oauth.description'),
      color: 'from-orange-500 to-red-500',
      animation: (
        <div className="mt-4 relative flex items-center justify-center gap-3">
          {/* Left icons */}
          <div className="flex flex-col gap-2">
            <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center animate-pulse" style={{ animationDelay: '0s' }}>
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center animate-pulse" style={{ animationDelay: '0.2s' }}>
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
          </div>

          {/* Center shield */}
          <div className="relative">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-400 rounded-2xl flex items-center justify-center shadow-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 border-4 border-orange-400 rounded-2xl animate-ping opacity-40"></div>
            </div>
          </div>

          {/* Right icons */}
          <div className="flex flex-col gap-2">
            <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center animate-pulse" style={{ animationDelay: '0.4s' }}>
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 1v6m0 6v6M5.6 5.6l4.2 4.2m4.4 4.4l4.2 4.2M1 12h6m6 0h6M5.6 18.4l4.2-4.2m4.4-4.4l4.2-4.2"/>
              </svg>
            </div>
            <div className="w-6 h-6 bg-cyan-100 rounded-lg flex items-center justify-center animate-pulse" style={{ animationDelay: '0.6s' }}>
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-cyan-600" fill="currentColor">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
              </svg>
            </div>
          </div>
        </div>
      )
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: t('landing.features.teamwork.title'),
      description: t('landing.features.teamwork.description'),
      color: 'from-indigo-500 to-purple-500',
      animation: (
        <div className="flex gap-2 mt-4 justify-center">
          {[
            {
              color: 'from-indigo-400 to-purple-400',
              icon: (
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              )
            },
            {
              color: 'from-blue-400 to-cyan-400',
              icon: (
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              )
            },
            {
              color: 'from-pink-400 to-rose-400',
              icon: (
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              )
            },
            {
              color: 'from-green-400 to-emerald-400',
              icon: (
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              )
            }
          ].map((member, i) => (
            <div
              key={i}
              className={`w-10 h-10 rounded-full bg-gradient-to-br ${member.color} animate-bounce flex items-center justify-center shadow-md`}
              style={{
                animationDelay: `${i * 0.15}s`,
                animationDuration: '1.5s'
              }}
            >
              {member.icon}
            </div>
          ))}
        </div>
      )
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: t('landing.features.calendar.title'),
      description: t('landing.features.calendar.description'),
      color: 'from-pink-500 to-rose-500',
      animation: (
        <div className="grid grid-cols-7 gap-1 mt-4">
          {[...Array(21)].map((_, i) => {
            const hasContent = i === 5 || i === 11 || i === 16;
            const delays = { 5: 0.3, 11: 0.8, 16: 1.5 };
            return (
              <div
                key={i}
                className={`h-4 rounded ${
                  hasContent
                    ? 'bg-gradient-to-br from-pink-400 to-rose-400 animate-calendar-glow'
                    : 'bg-gray-200'
                }`}
                style={hasContent ? { animationDelay: `${delays[i]}s` } : {}}
              ></div>
            );
          })}
        </div>
      )
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: t('landing.features.hashtags.title'),
      description: t('landing.features.hashtags.description'),
      color: 'from-cyan-500 to-blue-500',
      animation: (
        <div className="mt-4 h-8 overflow-hidden relative flex justify-center">
          <div
            key={visibleHashtagIndex}
            className="flex gap-2 animate-hashtag-slide-up"
          >
            {allHashtags[visibleHashtagIndex].map((tag, i) => (
              <div
                key={tag}
                className="bg-gradient-to-r from-cyan-100 to-blue-100 px-3 py-1.5 rounded-full text-xs font-semibold text-cyan-700 whitespace-nowrap flex items-center"
              >
                {tag}
              </div>
            ))}
          </div>
        </div>
      )
    }
  ];

  const pricingPlans = [
    {
      name: t('landing.pricing.free.name'),
      price: t('landing.pricing.free.price'),
      period: t('landing.pricing.free.period'),
      description: t('landing.pricing.free.description'),
      features: t('landing.pricing.free.features', { returnObjects: true }),
      cta: t('landing.pricing.free.cta'),
      highlighted: false,
      link: '/register'
    },
    {
      name: t('landing.pricing.pro.name'),
      price: t('landing.pricing.pro.price'),
      period: t('landing.pricing.pro.period'),
      description: t('landing.pricing.pro.description'),
      features: t('landing.pricing.pro.features', { returnObjects: true }),
      cta: t('landing.pricing.pro.cta'),
      highlighted: true,
      badge: t('landing.pricing.pro.badge'),
      link: '/register'
    },
    {
      name: t('landing.pricing.enterprise.name'),
      price: t('landing.pricing.enterprise.price'),
      period: t('landing.pricing.enterprise.period'),
      description: t('landing.pricing.enterprise.description'),
      features: t('landing.pricing.enterprise.features', { returnObjects: true }),
      cta: t('landing.pricing.enterprise.cta'),
      highlighted: false,
      link: '/register'
    }
  ];

  const stats = [
    { number: '10K+', label: t('landing.stats.activeUsers') },
    { number: '1M+', label: t('landing.stats.sharedPosts') },
    { number: '15+', label: t('landing.stats.platformSupport') },
    { number: '99.9%', label: t('landing.stats.uptime') }
  ];

  const platforms = [
    { name: 'TikTok', logo: '/logos/tiktok.png', dim: true },
    { name: 'YouTube', logo: '/logos/youtube.png', dim: true },
    { name: 'Facebook', logo: '/logos/facebook.png', dim: false },
    { name: 'Instagram', logo: '/logos/instagram.png', dim: false },
    { name: 'Twitter', logo: '/logos/twitter.png', dim: false },
    { name: 'LinkedIn', logo: '/logos/linkedin.png', dim: false },
    { name: 'Pinterest', logo: '/logos/pinterest.png', dim: true },
    { name: 'Telegram', logo: '/logos/telegram.png', dim: true }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-lg border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/logo.svg"
                alt="Social Octo Logo"
                className="w-10 h-10"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Social Octo
              </span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-700 hover:text-blue-600 font-medium transition">
                {t('nav.features')}
              </a>
              <a href="#pricing" className="text-gray-700 hover:text-blue-600 font-medium transition">
                {t('nav.pricing')}
              </a>

              {/* Dokümanlar Dropdown */}
              <div className="relative group">
                <button className="flex items-center gap-1 text-gray-700 hover:text-blue-600 font-medium transition py-2">
                  <FileText className="w-4 h-4" />
                  {t('nav.docs')}
                  <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                </button>

                {/* Dropdown Menu - görünmez köprü ile */}
                <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2">
                    <Link
                      to="/how-it-works"
                      className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                    >
                      <Zap className="w-5 h-5" />
                      <div>
                        <div className="font-medium">{t('nav.howItWorks')}</div>
                        <div className="text-xs text-gray-500">Adım adım kullanım kılavuzu</div>
                      </div>
                    </Link>
                    <Link
                      to="/platform-requirements"
                      className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                    >
                      <Shield className="w-5 h-5" />
                      <div>
                        <div className="font-medium">{t('nav.platformRequirements')}</div>
                        <div className="text-xs text-gray-500">Her platform için gereksinimler</div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>

              <LanguageSwitcher />

              <Link to="/login" className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 text-gray-700 hover:from-blue-600 hover:to-purple-600 hover:border-transparent hover:text-white font-medium transition px-5 py-1.5 rounded-lg">
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition"
              >
                {t('nav.register')}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col gap-4">
                <a href="#features" className="text-gray-700 hover:text-blue-600 font-medium transition">
                  {t('nav.features')}
                </a>
                <a href="#pricing" className="text-gray-700 hover:text-blue-600 font-medium transition">
                  {t('nav.pricing')}
                </a>

                {/* Dokümanlar Section */}
                <div className="border-t border-gray-200 pt-4 mt-2">
                  <div className="flex items-center gap-2 text-gray-500 font-semibold text-sm mb-3">
                    <FileText className="w-4 h-4" />
                    {t('nav.docs')}
                  </div>
                  <div className="pl-6 space-y-3">
                    <Link to="/how-it-works" className="flex items-start gap-2 text-gray-700 hover:text-blue-600 font-medium transition">
                      <Zap className="w-4 h-4 mt-0.5" />
                      <div>
                        <div>{t('nav.howItWorks')}</div>
                        <div className="text-xs text-gray-500">Adım adım kılavuz</div>
                      </div>
                    </Link>
                    <Link to="/platform-requirements" className="flex items-start gap-2 text-gray-700 hover:text-blue-600 font-medium transition">
                      <Shield className="w-4 h-4 mt-0.5" />
                      <div>
                        <div>{t('nav.platformRequirements')}</div>
                        <div className="text-xs text-gray-500">Gereksinimler listesi</div>
                      </div>
                    </Link>
                  </div>
                </div>

                <LanguageSwitcher />

                <Link to="/login" className="text-gray-700 hover:text-blue-600 font-medium transition pt-2 border-t border-gray-200">
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg font-semibold text-center"
                >
                  {t('nav.register')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <div className="inline-block mb-4">
              <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold">
                {t('landing.hero.badge')}
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              {t('landing.hero.title1')}{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {t('landing.hero.title2')}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8">
              {t('landing.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link
                to="/register"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition transform hover:scale-105 flex items-center justify-center gap-2"
              >
                {t('landing.hero.cta')}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/how-it-works"
                className="bg-white text-gray-700 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-lg transition border-2 border-gray-200"
              >
                {t('landing.hero.howItWorks')}
              </Link>
            </div>

            {/* Flow Animation */}
            <div className="mt-12">
              <FlowAnimation />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto mb-16">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Platform Logos */}
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <p className="text-center text-gray-600 font-semibold mb-6">{t('landing.platformsSupported')}</p>
            <div className="flex flex-wrap justify-center items-center gap-6">
              {platforms.map((platform, index) => (
                <div
                  key={index}
                  className={`transition ${platform.dim ? 'w-12 h-12 opacity-30 grayscale hover:opacity-60 hover:grayscale-0 hover:scale-110' : 'w-16 h-16 hover:scale-110'}`}
                >
                  <img src={platform.logo} alt={platform.name} className="w-full h-full object-contain" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>

          {/* Main Features - 2x2 Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {features.slice(0, 4).map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white border-2 border-gray-200 rounded-3xl p-8 hover:shadow-2xl transition-all duration-300 hover:border-transparent overflow-hidden"
              >
                {/* Gradient Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>

                <div className="relative z-10">
                  <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                    {feature.icon}
                  </div>
                  <h3 className={`text-2xl font-bold text-gray-900 mb-3 group-hover:bg-gradient-to-r ${feature.color} group-hover:bg-clip-text group-hover:text-transparent`}>
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    {feature.description}
                  </p>

                  {/* Animated Feature Preview */}
                  {feature.animation}
                </div>
              </div>
            ))}
          </div>

          {/* Secondary Features - 4 Column Grid */}
          <div className="grid md:grid-cols-4 gap-6">
            {features.slice(4, 8).map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white border-2 border-gray-200 rounded-3xl p-6 hover:shadow-2xl transition-all duration-300 hover:border-transparent overflow-hidden hover:-translate-y-2"
              >
                {/* Gradient Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>

                <div className="relative z-10 text-center">
                  <div className={`w-14 h-14 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center text-white mb-4 mx-auto group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>

                  {/* Animated Feature Preview */}
                  {feature.animation}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('landing.pricing.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('landing.pricing.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`bg-white rounded-3xl p-8 ${
                  plan.highlighted
                    ? 'ring-4 ring-blue-600 shadow-2xl scale-105'
                    : 'border-2 border-gray-200 shadow-lg'
                } relative`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600">{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={plan.link}
                  className={`block w-full py-3 rounded-xl font-bold text-center transition ${
                    plan.highlighted
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-xl'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t('landing.cta.title')}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {t('landing.cta.subtitle')}
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition transform hover:scale-105"
          >
            {t('landing.cta.button')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />

      {/* Custom CSS Animations */}
      <style jsx>{`
        @keyframes progress {
          0% {
            width: 0%;
          }
          100% {
            width: 100%;
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes grow {
          0% {
            transform: scaleY(0);
            opacity: 0;
          }
          100% {
            transform: scaleY(1);
            opacity: 1;
          }
        }

        @keyframes fade-in {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bar-pulse {
          0%, 100% {
            transform: scaleY(1);
            opacity: 1;
          }
          50% {
            transform: scaleY(0.7);
            opacity: 0.8;
          }
        }

        @keyframes calendar-glow {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 rgba(244, 114, 182, 0);
          }
          50% {
            transform: scale(1.15);
            box-shadow: 0 0 8px rgba(244, 114, 182, 0.8);
          }
        }

        @keyframes hashtag-slide-up {
          0% {
            transform: translateY(150%);
            opacity: 0;
          }
          15% {
            transform: translateY(0);
            opacity: 1;
          }
          85% {
            transform: translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateY(-100%);
            opacity: 0;
          }
        }

        .animate-progress {
          animation: progress 3s ease-in-out infinite;
        }

        .animate-hashtag-slide-up {
          animation: hashtag-slide-up 4s ease-in-out;
        }

        .animate-bar-pulse {
          transform-origin: bottom;
          animation: bar-pulse 3.5s ease-in-out infinite;
        }

        .animate-calendar-glow {
          animation: calendar-glow 2.5s ease-in-out infinite;
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .animate-grow {
          transform-origin: bottom;
          animation: grow 1s ease-out forwards;
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
